import type { ModuleResult, QuantitySummary } from '../gen/ejot/roofing/v1/types';
import { formatArea, formatCount, formatLength, formatQuantityWithUnit } from '../shared/format';
import './ResultReportSummary.css';

interface ResultReportSummaryProps {
  module: ModuleResult;
  quantities: QuantitySummary | undefined;
}

type SummaryRow = { label: string; value: string | undefined };

/**
 * Compact report-style summary composed only from backend-authored module
 * result values. The component deliberately omits fields the result contract
 * does not provide instead of inferring engineering values in the browser.
 */
export function ResultReportSummary({ module, quantities }: ResultReportSummaryProps) {
  const windInput = module.wind_input;
  const locationRows: SummaryRow[] = [
    { label: 'National wind dataset', value: windInput?.national_dataset_id },
    { label: 'Wind zone', value: windInput?.wind_zone_id },
    { label: 'Terrain category', value: windInput?.terrain_category_id },
    { label: 'Elevation above sea level', value: windInput?.elevation ? formatLength(windInput.elevation) : undefined },
    { label: 'Base wind velocity', value: windInput?.base_wind_velocity ? formatQuantityWithUnit(windInput.base_wind_velocity) : undefined },
  ];

  const roofRows: SummaryRow[] = [
    { label: 'Peak velocity pressure', value: module.wind?.peak_velocity_pressure ? formatQuantityWithUnit(module.wind.peak_velocity_pressure) : undefined },
    { label: 'Roof height above ground', value: windInput?.roof_height ? formatLength(windInput.roof_height) : undefined },
    { label: 'Roof slope', value: windInput?.roof_slope?.degrees === undefined ? undefined : `${windInput.roof_slope.degrees.toFixed(2)}°` },
    { label: 'Resistance basis', value: describeResistanceBasis(module.resistance_basis) },
    { label: 'Membrane area', value: quantities?.total_membrane_area ? formatArea(quantities.total_membrane_area) : undefined },
    { label: 'Fasteners - total', value: quantities?.total_fixings === undefined ? undefined : `${formatCount(quantities.total_fixings)} piece` },
  ];

  const productLines = quantities?.lines ?? [];
  for (const line of productLines) {
    const name = line.display_name || line.product_id;
    if (!name) continue;
    roofRows.push({
      label: 'Selected fixing system',
      value: `${name}${line.quantity === undefined ? '' : ` — ${formatLineQuantity(line.quantity, line.unit)}`}`,
    });
    break;
  }

  return (
    <section className="result-report-summary" aria-label="Engineering result summary">
      <h3 className="result-section-title">Summary</h3>
      <SummaryGroup title="Location" rows={locationRows} />
      <SummaryGroup title="Roof Parameters" rows={roofRows} />
    </section>
  );
}

function SummaryGroup({ title, rows }: { title: string; rows: SummaryRow[] }) {
  const visible = rows.filter((row) => row.value && row.value.trim() !== '');
  if (visible.length === 0) return null;
  return (
    <section className="result-report-summary-group">
      <h4>{title}</h4>
      <dl className="result-report-summary-grid">
        {visible.map((row) => (
          <div className="result-report-summary-row" key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function describeResistanceBasis(value: ModuleResult['resistance_basis']): string | undefined {
  switch (value) {
    case 'FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN':
      return 'Complete-system design resistance';
    case 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC':
      return 'ETA component characteristic resistance';
    case 'FIXING_RESISTANCE_BASIS_UNSPECIFIED':
    case undefined:
      return undefined;
    default:
      return value;
  }
}

function formatLineQuantity(quantity: number, unit: string | undefined): string {
  const rendered = Number.isInteger(quantity) ? formatCount(quantity) : quantity.toLocaleString('en-GB', { maximumFractionDigits: 2 });
  return unit ? `${rendered} ${unit}` : rendered;
}
